import type { ValuationMethod, ValueByValuationMethod } from '@einwert/valuation-api-client'
import { Stack } from '@mui/material'
import { DeviationAnalysis } from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationOverview/DeviationAnalysis'
import ValuationOverviewLoading from 'ClientPortal/src/pages/Assets/AssetDetails/Valuation/ValuationOverview/ValuationOverview.load'
import { isEinwertValuation } from 'ClientPortal/src/api/valuationModels'
import { TFunction } from 'i18next'
import {
    AllValues,
    CardItem,
    DataSplit,
    EmptyState,
    getAllValuesCopy,
    getFormattedValueAndUnitFromAreaValue,
    getFormattedValueAndUnitFromMonetaryValue,
    getFormattedValueAndUnitFromPercentage,
    getLeadingValuePerSqm,
    getLeadingValuePerSqmLabels,
    InfoTooltip,
    isKeyOf,
    KeyValueBox,
    BlockMath,
    LoadingSkeleton,
    MetricGrid,
    MultiStructuresInfoBox,
    PageGridContainer,
    PageGridItem,
    tokens,
    TrendKeyValueBox,
} from 'ComponentLibrary'
import { getFormattedValueAndUnitFromWault } from 'ComponentLibrary/src/common/wault'
import { getUsageTypeSplit } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetDetails.utils'
import { useValuationOverview } from './ValuationOverview.data'
import { isEmpty, sortBy } from 'lodash'
import { AllValuesExportCard } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetKeyValues/Widgets/AllValuesExportCard'
import { useTranslation } from 'react-i18next'
import { getMultiStructuresInfoBoxCopy } from 'ClientPortal/src/pages/Assets/AssetDetails/multiStructuresInfoBoxCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

const SUPPORTED_MARKET_VALUE_VALUATION_METHODS: Record<ValuationMethod, true> = {
    land_value: true,
    income: true,
    investment_method: true,
    comparison: true,
    cost: true,
    discounted_cashflow: true,
    custom: true,
    leasehold: true,
}

export default function ValuationOverview() {
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tAllValues } = useTranslation('terminology', { keyPrefix: 'export.allValues' })
    const { t: tTenantUsageTypes } = useTranslation('terminology', { keyPrefix: 'common.tenantUsageTypes' })
    const { t } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.valuation.valuationOverview' })
    const { t: tAllValuesPage } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.assetKeyValues.allValues' })
    const locale = useCurrentResolvedLocale()
    const {
        showDeviationAnalysis,
        valuationOptions,
        groupedOptionsByLabel,
        assetTitle,
        allStructures,
        isValuationsPending,
        isAssetPending,
        hasGroupedValuations,
        valuation,
        isValuationPending,
    } = useValuationOverview()

    if (isValuationPending || isAssetPending) {
        return <ValuationOverviewLoading />
    }

    if (valuation === undefined) {
        return (
            <PageGridContainer>
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <EmptyState size="enormous" style={{ marginTop: '12vh' }} title={t('emptyStateTitle')} />
                </PageGridItem>
            </PageGridContainer>
        )
    }

    const hasIncomeApproach = valuation.market_value_by_valuation_method.income
    const hasInvestmentMethod = valuation.selected_valuation_methods.includes('investment_method')
    const usageTypeSplit = getUsageTypeSplit(valuation.tenant_summary, tTenantUsageTypes)
    const multiStructuresInfoBoxCopy = getMultiStructuresInfoBoxCopy(tTerminology)
    const { valuePerSqmValue, valuePerSqmLabel } = getLeadingValuePerSqm(valuation, getLeadingValuePerSqmLabels(tTerminology))
    const allValuesCopy = getAllValuesCopy(tTerminology, tAllValues, tAllValuesPage)
    const methodLabel = isEinwertValuation(valuation) ? tTerminology('leadingMethod') : tTerminology('usedMethods')
    const methodValue = isEinwertValuation(valuation)
        ? getValuationMethodDisplayValue(valuation.economic_units[0].chosen_market_valuation_method, tTerminology)
        : getUsedValuationMethods(valuation.market_value_by_valuation_method, tTerminology).join(', ')

    return (
        <PageGridContainer>
            <PageGridItem columnSpan={3} columnSpanXs={4}>
                <Stack direction="column" gap={tokens.SizingSpacing300}>
                    <Stack direction="row" gap={tokens.SizingSpacing300}>
                        <CardItem>
                            {valuation.market_value_with_trend.trend ? (
                                <TrendKeyValueBox
                                    label={tTerminology('valueTrend')}
                                    value={valuation.market_value_with_trend.trend * 100}
                                    unit="%"
                                    locale={locale}
                                />
                            ) : (
                                <KeyValueBox label={tTerminology('valueTrend')} size="small" value="-" />
                            )}
                        </CardItem>
                        <CardItem>
                            <KeyValueBox
                                label={valuePerSqmLabel}
                                size="small"
                                {...getFormattedValueAndUnitFromMonetaryValue(valuePerSqmValue, locale)}
                            />
                        </CardItem>
                    </Stack>
                    <Stack direction="row" gap={tokens.SizingSpacing300}>
                        <CardItem>
                            <KeyValueBox
                                label={tTerminology('marketValue')}
                                size="small"
                                {...getFormattedValueAndUnitFromMonetaryValue(valuation.market_value, locale)}
                            />
                        </CardItem>
                        <CardItem>
                            <KeyValueBox
                                label={tTerminology('rentalIncome')}
                                size="small"
                                {...getFormattedValueAndUnitFromMonetaryValue(
                                    valuation.tenant_summary.total?.actual_rental_income.total,
                                    locale
                                )}
                                dataTestId="rental-income-value"
                            />
                        </CardItem>
                    </Stack>
                </Stack>
            </PageGridItem>
            {hasInvestmentMethod && (
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <Stack direction="column" gap={tokens.SizingSpacing300}>
                        <Stack direction="row" gap={tokens.SizingSpacing300}>
                            <CardItem>
                                <KeyValueBox
                                    label={tTerminology('grossCapitalValue')}
                                    size="small"
                                    {...getFormattedValueAndUnitFromMonetaryValue(valuation.gross_capital_value, locale)}
                                />
                            </CardItem>
                            <CardItem>
                                <KeyValueBox
                                    label={tTerminology('netCapitalValue')}
                                    size="small"
                                    {...getFormattedValueAndUnitFromMonetaryValue(valuation.net_capital_value, locale)}
                                />
                            </CardItem>
                        </Stack>
                        <Stack direction="row" gap={tokens.SizingSpacing300}>
                            <CardItem>
                                <KeyValueBox
                                    label={tTerminology('netYield')}
                                    size="small"
                                    {...getFormattedValueAndUnitFromPercentage(valuation.net_yield, undefined, locale)}
                                />
                            </CardItem>
                            <CardItem>
                                <KeyValueBox
                                    label={tTerminology('ancillaryAcquisitionCosts')}
                                    size="small"
                                    {...getFormattedValueAndUnitFromMonetaryValue(valuation.ancillary_acquisition_costs, locale)}
                                />
                            </CardItem>
                        </Stack>
                    </Stack>
                </PageGridItem>
            )}
            {hasIncomeApproach && (
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem height="242px">
                        <DataSplit
                            title={tTerminology('marketRent')}
                            data={usageTypeSplit}
                            valueLabel={tTerminology('rent')}
                            percentageLabel={tTerminology('share')}
                            size="medium"
                            emptyStateTitle={tTerminology('notEnoughDataAvailableYet')}
                            remainingLegendLabel={tTerminology('remaining')}
                            locale={locale}
                        />
                    </CardItem>
                </PageGridItem>
            )}
            <PageGridItem columnSpan={3} columnSpanXs={4} rowSpan={1} rowSpanXs={1}>
                <CardItem>
                    {hasIncomeApproach ? (
                        <MetricGrid
                            size="small"
                            numColumns={3}
                            values={[
                                {
                                    subheadline: methodLabel,
                                    value: methodValue,
                                },
                                {
                                    subheadline: tTerminology('grossInitialYield'),
                                    ...getFormattedValueAndUnitFromPercentage(valuation.gross_initial_yield, undefined, locale),
                                    endAdornment: (
                                        <InfoTooltip
                                            text={<BlockMath math={t('formulas.grossInitialYield')} />}
                                            size={tokens.SizingIconS}
                                        />
                                    ),
                                },
                                {
                                    subheadline: tTerminology('grossYield'),
                                    ...getFormattedValueAndUnitFromPercentage(valuation.gross_yield, undefined, locale),
                                    endAdornment: (
                                        <InfoTooltip text={<BlockMath math={t('formulas.grossYield')} />} size={tokens.SizingIconS} />
                                    ),
                                },
                            ]}
                        />
                    ) : (
                        <KeyValueBox label={methodLabel} value={methodValue} size="small" />
                    )}
                </CardItem>
            </PageGridItem>
            {hasIncomeApproach && (
                <PageGridItem columnSpan={3} columnSpanXs={4}>
                    <CardItem>
                        <MetricGrid
                            data-testid="rent-kpi-grid"
                            size="small"
                            numColumns={3}
                            values={[
                                {
                                    ...getFormattedValueAndUnitFromMonetaryValue(
                                        valuation.tenant_summary.total?.gross_rental_income.total,
                                        locale
                                    ),
                                    subheadline: tTerminology('marketRent'),
                                },
                                {
                                    ...getFormattedValueAndUnitFromAreaValue(valuation.rentable_area, locale),
                                    subheadline: tTerminology('rentableArea'),
                                },
                                {
                                    ...getFormattedValueAndUnitFromWault(
                                        valuation.wault,
                                        {
                                            unknown: tTerminology('unknown'),
                                            unlimited: tTerminology('unlimited'),
                                            year: tTerminology('year'),
                                            years: tTerminology('years'),
                                        },
                                        locale
                                    ),
                                    color: valuation.wault === undefined ? tokens.ColorTextSubtlest : undefined,
                                    subheadline: tTerminology('wault'),
                                },
                            ]}
                        />
                    </CardItem>
                </PageGridItem>
            )}
            {isValuationsPending ? (
                <PageGridItem columnSpan={6} columnSpanXs={4}>
                    <LoadingSkeleton height="320px" sx={{ transform: 'none', borderRadius: '12px' }} />
                </PageGridItem>
            ) : showDeviationAnalysis ? (
                <DeviationAnalysis
                    selectedValuation={valuation}
                    valuationOptions={valuationOptions}
                    groupedValuationOptions={groupedOptionsByLabel}
                    hasGroupedValuations={hasGroupedValuations}
                    assetTitle={assetTitle}
                    structures={allStructures}
                    locale={locale}
                />
            ) : (
                <>
                    <PageGridItem columnSpan={4} columnSpanXs={4}>
                        <CardItem>
                            <AllValues valuation={valuation} copy={allValuesCopy} locale={locale} />
                        </CardItem>
                    </PageGridItem>
                    <PageGridItem columnSpan={2} columnSpanXs={2}>
                        <Stack position="sticky" gap={tokens.SizingSpacing300} top={tokens.SizingSpacing200} height="fit-content">
                            <AllValuesExportCard valuation={valuation} addressText={assetTitle} />
                            {!isEmpty(allStructures) && (
                                <MultiStructuresInfoBox structures={allStructures} copy={multiStructuresInfoBoxCopy} />
                            )}
                        </Stack>
                    </PageGridItem>
                </>
            )}
        </PageGridContainer>
    )
}

function getValuationMethodDisplayValue(method: ValuationMethod | undefined, tTerminology: TFunction) {
    if (method === undefined) {
        return '-'
    }

    return tTerminology(`valuationMethods.${method}`)
}

function getUsedValuationMethods(market_value_by_valuation_method: ValueByValuationMethod, tTerminology: TFunction): string[] {
    const usedMethods = Object.keys(market_value_by_valuation_method).filter((key) =>
        isKeyOf(key, SUPPORTED_MARKET_VALUE_VALUATION_METHODS)
    )

    if (usedMethods.includes('land_value') && usedMethods.length > 1) {
        usedMethods.splice(usedMethods.indexOf('land_value'), 1)
    }

    const sortedMethods = sortBy(usedMethods, (method) => {
        if (method === 'investment_method') {
            return 0
        }
        if (method === 'income') {
            return 1
        }
        return 2
    })

    return sortedMethods.map((method) => getValuationMethodDisplayValue(method, tTerminology))
}
